import type { ReactNode } from 'react'
import type { CSSVars } from '@/types/css'

import { useEffect, useRef } from 'react'
import { X } from 'lucide-react'
import { useRouter } from 'next/router'
import { cn } from '@/lib/utils'
import { FadeLink } from '@/components/generals/fade-link'
import { StarDrift } from '@/components/generals/star-drift'
import { Wordmark } from '@/components/generals/wordmark'
import { RedLightToggle, SoundToggle } from '@/components/generals/toggles'
import { primaryNav, secondaryNav } from '@/constants/site'

interface MobileMenuProps {
    open: boolean
    onClose: () => void
}

export const MobileMenu = ({ open, onClose }: MobileMenuProps): ReactNode => {
    const router = useRouter()
    const closeRef = useRef<HTMLButtonElement | null>(null)
    const panelRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        if (!open) return

        const previous = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        closeRef.current?.focus()

        const handleKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose()

            if (event.key === 'Tab' && panelRef.current) {
                const focusable = panelRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled])')
                const first = focusable[0]
                const last = focusable[focusable.length - 1]

                if (event.shiftKey && document.activeElement === first) {
                    event.preventDefault()
                    last?.focus()
                }
                else if (!event.shiftKey && document.activeElement === last) {
                    event.preventDefault()
                    first?.focus()
                }
            }
        }

        document.addEventListener('keydown', handleKey)

        return () => {
            document.body.style.overflow = previous
            document.removeEventListener('keydown', handleKey)
        }
    }, [open, onClose])

    useEffect(() => {
        router.events.on('routeChangeStart', onClose)

        return () => router.events.off('routeChangeStart', onClose)
    }, [router, onClose])

    return (
        <div
            ref={panelRef}
            id='mobile-menu'
            role='dialog'
            aria-modal='true'
            aria-label='Site menu'
            className={cn(
                'fixed inset-0 z-[70] flex flex-col bg-ink transition-[opacity,visibility] duration-700 xl:hidden',
                open ? 'visible opacity-100' : 'invisible opacity-0'
            )}
        >
            {open && <StarDrift className='absolute inset-0' density={0.0003} speed={0.8} />}

            <div className='relative flex h-16 items-center justify-between px-5'>
                <Wordmark />
                <button
                    ref={closeRef}
                    type='button'
                    onClick={onClose}
                    className='inline-flex size-10 items-center justify-center rounded-full border border-hair-2 text-star'
                    aria-label='Close menu'
                >
                    <X aria-hidden className='size-4' strokeWidth={1.25} />
                </button>
            </div>

            <nav aria-label='Primary' className='relative flex-1 overflow-y-auto px-5 pb-8 pt-6'>
                <ul>
                    {primaryNav.map((link, index) => {
                        const style: CSSVars = { transitionDelay: open ? `${120 + index * 70}ms` : '0ms' }

                        return (
                            <li
                                key={link.href}
                                style={style}
                                className={cn('border-b border-hair transition-all duration-700', open ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0')}
                            >
                                <FadeLink href={link.href} className='flex items-baseline gap-4 py-4'>
                                    <span className='label-mono w-6 text-brass'>
                                        {String(index + 1).padStart(2, '0')}
                                    </span>
                                    <span className='font-heading text-[2.6rem] font-light leading-none text-star'>
                                        {link.label}
                                    </span>
                                </FadeLink>
                            </li>
                        )
                    })}
                </ul>

                <ul className='mt-8 flex flex-wrap gap-x-6 gap-y-3'>
                    {secondaryNav.map(link => (
                        <li key={link.href}>
                            <FadeLink href={link.href} className='label-mono text-star-2 hover:text-star'>
                                {link.label}
                            </FadeLink>
                        </li>
                    ))}
                </ul>
            </nav>

            <div className='relative flex flex-wrap items-center justify-between gap-4 border-t border-hair px-5 py-5'>
                <div className='flex items-center gap-2'>
                    <RedLightToggle className='-ml-3' />
                    <SoundToggle />
                </div>
                <FadeLink
                    href='/shows'
                    className='label-mono inline-flex h-10 items-center rounded-full bg-brass px-6 text-ink'
                >
                    Book a show
                </FadeLink>
            </div>
        </div>
    )
}
